import {
  ACCESSORY_ROWS,
  QUANTITY_ROWS,
  catalogRowSuffix,
  type ServiceAgreementQuantityRow,
} from './serviceAgreementLayout';

/**
 * Per-contract catalog pricing.
 *
 * The agreement's two quantity lists (monthly robots, one-time accessories)
 * have default prices in `serviceAgreementLayout.mjs`. A deal can negotiate its
 * own price for any line, stored on the contract as `form_data.unitPrices` and
 * keyed by the same `qty*` field key. Nothing here mutates the catalog: an
 * override applies to that one contract, and a line with no override always
 * falls back to the current default.
 */

export type ContractSection = 'monthly' | 'accessory';

/** Price overrides for one contract, keyed by qty field key. */
export type ContractUnitPrices = Record<string, number>;

/**
 * Anything carrying the contract's `qty*` fields — the form state on the order
 * page or the stored form data on the signing page. Typed loosely because both
 * of those are interfaces, and TypeScript will not match an interface against
 * an index signature; `readQty` does the one cast needed to read a field.
 */
export type ContractQuantities = object;

/** Read one `qty*` field as a number, treating blank or junk as zero. */
export function readQty(quantities: ContractQuantities, key: string): number {
  const raw = (quantities as Record<string, unknown>)[key];
  return parseInt(String(raw ?? ''), 10) || 0;
}

export const CATALOG_ROWS: Record<ContractSection, ServiceAgreementQuantityRow[]> = {
  monthly: QUANTITY_ROWS,
  accessory: ACCESSORY_ROWS,
};

/** Every catalog row, by qty field key. */
const ROW_BY_KEY = new Map<string, ServiceAgreementQuantityRow>(
  [...QUANTITY_ROWS, ...ACCESSORY_ROWS].map((row) => [row.key, row])
);

/** The catalog default for a key, or 0 if the key is not a catalog line. */
export function defaultUnitPrice(key: string): number {
  const row = ROW_BY_KEY.get(key);
  return row ? row.price : 0;
}

/** The price this contract charges for a catalog line. */
export function unitPriceFor(key: string, overrides?: ContractUnitPrices | null): number {
  const override = overrides?.[key];
  return typeof override === 'number' && Number.isFinite(override) && override >= 0
    ? override
    : defaultUnitPrice(key);
}

/** The label shown on the form and printed on the PDF, e.g. "Base - Metal ($15 per base)". */
export function catalogRowLabel(
  row: ServiceAgreementQuantityRow,
  overrides?: ContractUnitPrices | null
): string {
  return `${row.label} ${catalogRowSuffix(row, unitPriceFor(row.key, overrides))}`;
}

/**
 * Sum of qty × effective price for one section's catalog lines. Custom products
 * are added separately by the caller (see `customProductsTotal`).
 */
export function catalogSectionTotal(
  section: ContractSection,
  quantities: ContractQuantities,
  overrides?: ContractUnitPrices | null
): number {
  return CATALOG_ROWS[section].reduce(
    (total, row) => total + readQty(quantities, row.key) * unitPriceFor(row.key, overrides),
    0
  );
}

/**
 * Read `unitPrices` back off a contract's stored form data. Comes through as an
 * object in the browser and can arrive as a JSON string from the API, and only
 * keys that are real catalog lines are kept.
 */
export function parseUnitPrices(value: unknown): ContractUnitPrices {
  const raw = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const prices: ContractUnitPrices = {};
  for (const [key, entry] of Object.entries(raw as Record<string, unknown>)) {
    if (!ROW_BY_KEY.has(key)) continue;
    const amount = Number(String(entry ?? '').replace(/[$,]/g, ''));
    if (Number.isFinite(amount) && amount >= 0) prices[key] = amount;
  }
  return prices;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
